import { useEffect, useRef, useState } from 'react';

import {
  ClientOnboardingStatusType,
  CountryEntity,
  OnboardingStatusType,
} from 'api/types/entity';
import { isFinanceProject, isGlobalProject } from 'config';
import { lodash } from 'helpers';
import {
  LocationCountryFilter,
  RegistrationCountryFilter,
  RiskLevelFilter,
  StatusFilter,
} from 'modules/client/common/components';

import { ClientFilterType, RiskLevelFilterType } from '../../types';

interface Props {
  filter: ClientFilterType;
  registrationCountries?: CountryEntity[];
  locationCountries?: CountryEntity[];
  onApply: (f: ClientFilterType) => void;
}

export const ClientsFilters = ({
  filter,
  registrationCountries,
  locationCountries,
  onApply,
}: Props) => {
  const isMounted = useRef(false);

  const isGlobal = isGlobalProject();
  const isFinance = isFinanceProject();

  const [statuses, setStatuses] = useState<ClientOnboardingStatusType[]>(
    filter.onboardingStatuses ?? []
  );

  const [riskLevels, setRiskLevels] = useState<RiskLevelFilterType[]>(
    filter.riskLevels ?? []
  );

  const [selectedRegistrationCountries, setSelectedRegistrationCountries] =
    useState<string[]>(filter.registrationCountries ?? []);

  const [selectedLocationCountries, setSelectedLocationCountries] = useState<
    string[]
  >(filter.locationCountries ?? []);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    onApply({
      onboardingStatuses: statuses.length ? statuses : undefined,
      riskLevels: riskLevels.length ? riskLevels : undefined,
      registrationCountries: selectedRegistrationCountries.length
        ? selectedRegistrationCountries
        : undefined,
      locationCountries: selectedLocationCountries.length
        ? selectedLocationCountries
        : undefined,
    });
  }, [
    onApply,
    riskLevels,
    selectedLocationCountries,
    selectedRegistrationCountries,
    statuses,
  ]);

  return (
    <>
      <StatusFilter
        excludeStatuses={lodash.compact([
          isFinance && OnboardingStatusType.AwaitingPaymentConfirmation,
          isGlobal && OnboardingStatusType.PendingBoardReview,
          isGlobal && OnboardingStatusType.BoardReview,
        ])}
        setStatuses={setStatuses}
        statuses={statuses}
      />
      <RiskLevelFilter riskLevels={riskLevels} setRiskLevels={setRiskLevels} />

      {isFinance && (
        <RegistrationCountryFilter
          countries={registrationCountries ?? []}
          selectedCountries={selectedRegistrationCountries}
          setSelectedCountries={setSelectedRegistrationCountries}
        />
      )}
      <LocationCountryFilter
        countries={locationCountries ?? []}
        selectedCountries={selectedLocationCountries}
        setSelectedCountries={setSelectedLocationCountries}
      />
    </>
  );
};
